import { getPageContext, PageContextOptions } from '@thoughtspot/blink-context';
import { HostEventContract, IncomingEventType } from '@thoughtspot/embed-util';
import { useCallback, useEffect } from 'react';
import {
    EventHandler,
    logger,
    MessageType,
    rootLevelEvents,
} from './constants';
import { dispatchOneWayEvent } from './handlers';

export const eventHandlerMap = new Map<string, Set<EventHandler>>();

export const contextsMatch = (ctx1: any, ctx2: any): boolean => {
    if (ctx1 === ctx2) return true;

    if (ctx1 === undefined || ctx1 === null) {
        return ctx2 === undefined || ctx2 === null;
    }

    if (typeof ctx1 === 'object' && typeof ctx2 === 'object') {
        return ctx1.page === ctx2.page;
    }

    return false;
};

export const unsubscribe = (
    type: string,
    callback: (payload: any) => any | Promise<any>,
    context?: any,
) => {
    const handlers = eventHandlerMap.get(type);
    if (!handlers) return;

    const handlerToRemove = Array.from(handlers).find(h => {
        return (
            h.callback === callback &&
            (context !== undefined ? contextsMatch(h.context, context) : true)
        );
    });

    if (handlerToRemove) {
        handlers.delete(handlerToRemove);
        logger.debug(`[unsubscribe] Removed handler for ${type}`, {
            context: handlerToRemove.context,
        });
    }

    if (handlers.size === 0) {
        eventHandlerMap.delete(type);
    }
};

export const subscribe = (
    type: string,
    callback: (...args: any[]) => any | Promise<any>,
    forceDelete = false,
    passedContext?: PageContextOptions,
): (() => void) => {
    if (!type) {
        return () => undefined;
    }

    if (forceDelete) {
        eventHandlerMap.delete(type);
    }

    const isRootLevel = rootLevelEvents.has(type as IncomingEventType);
    let context = passedContext;
    if (!context) {
        try {
            context = getPageContext();
        } catch {
            // getPageContext may not be available in test/SSR environments
            context = undefined;
        }
    }

    if (!eventHandlerMap.has(type)) {
        eventHandlerMap.set(type, new Set<EventHandler>());
    }

    const handlers = eventHandlerMap.get(type)!;

    // Root-level handlers are stored under 'root'; look them up and
    // unsubscribe by that same value, else removal never matches (leak).
    const storedContext = isRootLevel ? 'root' : context;

    const existingHandler = Array.from(handlers).find(
        h => h.callback === callback && contextsMatch(h.context, storedContext),
    );

    if (existingHandler) {
        return () => unsubscribe(type, callback, storedContext);
    }

    if (!isRootLevel && !context) {
        logger.warn(
            `[subscribe] No context for ${type}, handler not registered`,
        );
        return () => undefined;
    }

    // Get the schema for this event type from HostEventContract
    // HostEventContract[type] returns { [context]: ContractEntry }
    const eventSchemas = HostEventContract[type] || {};

    handlers.add({
        callback,
        context: storedContext,
        requiredParams: eventSchemas,
    });

    // One-way announcement — no reply expected (see dispatchOneWayEvent).
    dispatchOneWayEvent(`${type} Subscribed`, { type, context });

    return () => unsubscribe(type, callback, storedContext);
};

export const createEventSubscriber = () => (
    type: MessageType,
    callback: (payload: any) => any | Promise<any>,
    deps: any[] = [],
) => {
    const stableCallback = useCallback(callback, deps);

    useEffect(() => {
        const unsubscribeFn = subscribe(type, stableCallback);
        return unsubscribeFn;
    }, [type, stableCallback]);
};

/** Registers the same callback under every id in `types`. */
export const subscribeMultiple = (
    types: string[],
    callback: (...args: any[]) => any | Promise<any>,
    forceDelete = false,
    passedContext?: PageContextOptions,
): (() => void) => {
    const unsubscribers = types.map(type =>
        subscribe(type, callback, forceDelete, passedContext),
    );
    return () => unsubscribers.forEach(unsub => unsub());
};

/** Hook version of subscribeMultiple — one useCallback/useEffect pair. */
export const createMultipleEventSubscriber = () => (
    types: string[],
    callback: (payload: any) => any | Promise<any>,
    deps: any[] = [],
) => {
    const stableCallback = useCallback(callback, deps);
    // Stands in for `types` in the deps array below, since callers pass a
    // fresh array literal every render.
    const typesKey = types.join(',');

    useEffect(() => {
        const unsubscribers = types.map(type =>
            subscribe(type, stableCallback),
        );
        return () => unsubscribers.forEach(unsub => unsub());
    }, [typesKey, stableCallback]);
};
